import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Save, BookOpen, ChevronDown, ChevronUp, Clapperboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { createFormSave } from '../lib/formAutosave';
import { useFormAutosave } from '../hooks/useFormAutosave';
import SaveStatusLabel from '../components/SaveStatusLabel';
import {
  LEVEL_CONFIG, FORGE_GUIDE_KEYS, EPISODE_FORGE_GUIDE, SHORT_FORGE_GUIDE,
  PILOT_FORGE_GUIDE,
} from '../lib/plotBeats';
import { SCROLL_CANVAS } from '../lib/featureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Route collapse (SCROLL_CANVAS): the level wizard becomes a redirect shim
// into the workspace plot tab, scrolled to the requested beat. Deep links
// and every existing navigate() caller keep working. The wizard lives in
// its own component so the shim's early return never sits above its hooks.
export default function PlotLevel() {
  const { projectId, levelNum } = useParams();
  const location = useLocation();
  if (SCROLL_CANVAS) {
    return (
      <Navigate
        to={`/project/${projectId}?tab=plot`}
        replace
        state={{
          scrollToLevel: parseInt(levelNum, 10) || 1,
          scrollToBeatIndex: location.state?.initialBeatIndex ?? 0,
        }}
      />
    );
  }
  return <PlotLevelWizard />;
}

function PlotLevelWizard() {
  const { projectId, levelNum } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); // completion in flight
  const [stressWarning, setStressWarning] = useState(null);
  const saveClientRef = useRef(null);
  const [showForgeGuide, setShowForgeGuide] = useState(false);
  const [activeBeatIndex, setActiveBeatIndex] = useState(
    location.state?.initialBeatIndex ?? 0
  );

  const currentLevel = parseInt(levelNum);
  const levelConfig = LEVEL_CONFIG[currentLevel];
  const LevelIcon = levelConfig?.icon;

  useEffect(() => {
    fetchProject();
    if (currentLevel === 2) {
      fetchEnneagramWarning();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, levelNum]);

  // Reset beat index when act changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveBeatIndex(location.state?.initialBeatIndex ?? 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelNum]);

  const fetchProject = async () => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`load failed (${response.status})`);
      const data = await response.json();
      setProject(data);
      const levelKey = `level_${currentLevel}`;
      setFormData(data[levelKey] || {});
      // Re-key the save client when the project changes (param-only route
      // changes don't remount) — a stale client would PUT this project's
      // data at the OLD project's URL. Never regress a live client's rev
      // from a fetch: a GET served before our own in-flight PUT landed
      // carries the pre-rotation rev and adopting it 409-deadlocks saves.
      if (saveClientRef.current?.projectId !== projectId) {
        saveClientRef.current = {
          projectId,
          client: createFormSave({
            url: `${API}/projects/${projectId}`,
            initialRev: data.content_rev ?? null,
          }),
        };
      } else if (saveClientRef.current.client.getRev() == null) {
        saveClientRef.current.client.setRev(data.content_rev ?? null);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      // Disable autosave against a page we failed to load — otherwise the
      // previous level's formData could be saved under this level's key.
      setProject(null);
      setFormData({});
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnneagramWarning = async () => {
    try {
      const projectsRes = await authFetch(`${API}/projects`, { credentials: 'include' });
      const allProjects = await projectsRes.json();
      const protagonist = allProjects.find(p =>
        p.project_type === 'character' &&
        p.character_role === 'protagonist' &&
        p.enneagram_type
      );
      if (protagonist?.enneagram_type) {
        const enneagramRes = await authFetch(`${API}/enneagram/${protagonist.project_id}`, {
          credentials: 'include'
        });
        const enneagramData = await enneagramRes.json();
        setStressWarning(enneagramData);
      }
    } catch (error) {
      console.error('Error fetching enneagram:', error);
    }
  };

  // The autosaved payload pins the level it belongs to: the saver is
  // recreated when currentLevel changes (via the useCallback dep), and the
  // outgoing saver's final flush still holds the OLD level + OLD form data.
  const persistLevel = useCallback(
    (data, meta) =>
      saveClientRef.current.client.save({ level: currentLevel, data, completed: false }, meta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, currentLevel],
  );
  const { status: saveStatus, markDirty, flush, isDirty } = useFormAutosave({
    data: formData,
    save: persistLevel,
    enabled: !loading && !!project,
  });

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    markDirty();
  };

  // Explicit flush — autosave already persists on a debounce; this is the
  // "save right now" button, not the only path to persistence.
  const handleSave = () => flush();

  const handleComplete = async () => {
    setSaving(true);
    try {
      await flush(); // latest edits land before the completion write
      const client = saveClientRef.current?.client;
      const response = await authFetch(`${API}/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          level: currentLevel,
          data: formData,
          completed: true,
          base_rev: client?.getRev() ?? null,
        }),
      });
      if (response.status === 409) {
        toast.error(t('saveStatus_conflict'));
        return;
      }
      if (!response.ok) throw new Error('Failed to complete plot');
      const updated = await response.json().catch(() => ({}));
      client?.setRev(updated.content_rev ?? null);
      toast.success('Plot completed!');
      setTimeout(() => navigate('/dashboard?type=plot'), 1500);
    } catch (error) {
      console.error('Error completing plot:', error);
      toast.error('Failed to complete plot');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !levelConfig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  // Normalise legacy format values stored in MongoDB before LEVEL_CONFIG lookup
  const _FORMAT_ALIAS = { short_film: 'short', feature_film: 'feature' };
  const rawFormat = project?.format || 'feature';
  const formatKey = _FORMAT_ALIAS[rawFormat] || rawFormat;
  const beats = levelConfig.beats[formatKey] || levelConfig.beats.feature;
  const actTitle = levelConfig.formatTitles?.[formatKey]?.title || levelConfig.title;
  const actSubtitle = levelConfig.formatTitles?.[formatKey]?.subtitle || levelConfig.subtitle;

  // Clamp activeBeatIndex to valid range — guards against cross-format/cross-act mismatches
  const safeIndex = Math.max(0, Math.min(activeBeatIndex, beats.length - 1));

  // Compute global beat position across all acts for "Beat X of N"
  const allBeats = [1, 2, 3].flatMap(lvl =>
    LEVEL_CONFIG[lvl].beats[formatKey] || LEVEL_CONFIG[lvl].beats.feature
  );
  const totalBeats = allBeats.length;
  const beatsBeforeThisAct = [1, 2, 3]
    .slice(0, currentLevel - 1)
    .reduce((sum, lvl) => sum + (LEVEL_CONFIG[lvl].beats[formatKey] || LEVEL_CONFIG[lvl].beats.feature).length, 0);
  const globalBeatIndex = beatsBeforeThisAct + safeIndex;

  const isFirstBeat = globalBeatIndex === 0;
  const isLastBeat = globalBeatIndex === totalBeats - 1;

  const currentBeat = beats[safeIndex];
  const nextBeatInAct = beats[safeIndex + 1];

  // Safety net — should never happen after clamping, but prevents a white screen
  if (!currentBeat) return null;

  // Next beat label (may be in a future act)
  let nextBeatLabel = null;
  if (!isLastBeat) {
    if (nextBeatInAct) {
      nextBeatLabel = nextBeatInAct.label;
    } else {
      const nextActBeats = LEVEL_CONFIG[currentLevel + 1]?.beats[formatKey] ||
        LEVEL_CONFIG[currentLevel + 1]?.beats.feature;
      nextBeatLabel = nextActBeats?.[0]?.label ?? null;
    }
  }

  const handlePrev = async () => {
    await flush();
    if (safeIndex > 0) {
      setActiveBeatIndex(safeIndex - 1); // same act — formData survives in state
    } else {
      // Crossing acts refetches formData; leaving with a failed flush would
      // silently drop the unsaved beat. Stay put and tell the writer.
      if (isDirty()) {
        toast.error(t('autosave_navBlocked'));
        return;
      }
      const prevActBeats = LEVEL_CONFIG[currentLevel - 1]?.beats[formatKey] ||
        LEVEL_CONFIG[currentLevel - 1]?.beats.feature;
      navigate(`/plot/${projectId}/level/${currentLevel - 1}`, {
        state: { initialBeatIndex: (prevActBeats?.length ?? 1) - 1 },
      });
    }
  };

  const handleNext = async () => {
    if (isLastBeat) {
      await handleComplete();
      return;
    }
    await flush();
    if (safeIndex < beats.length - 1) {
      setActiveBeatIndex(safeIndex + 1); // same act — formData survives in state
    } else {
      if (isDirty()) {
        toast.error(t('autosave_navBlocked'));
        return;
      }
      // First beat of next act
      navigate(`/plot/${projectId}/level/${currentLevel + 1}`, {
        state: { initialBeatIndex: 0 },
      });
    }
  };

  const guideKey = FORGE_GUIDE_KEYS[currentBeat.key];
  const guideText = guideKey === '__episode__'
    ? (EPISODE_FORGE_GUIDE[currentBeat.key] || null)
    : guideKey === '__short__'
    ? (SHORT_FORGE_GUIDE[currentBeat.key] || null)
    : guideKey === '__pilot__'
    ? (PILOT_FORGE_GUIDE[currentBeat.key] || null)
    : (guideKey ? t(guideKey) : null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/project/${projectId}?tab=plot`)}
            data-testid="back-to-workspace-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workspace
          </Button>
          <div className="text-sm font-mono" data-testid="project-title">
            {project?.title}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-primary/30 rounded-full flex items-center justify-center">
                <LevelIcon className="w-10 h-10 text-primary" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center font-mono text-sm font-bold">
                {currentLevel}
              </div>
            </div>
          </div>
          <h1 className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-testid="level-title">
            {actTitle}
          </h1>
          <p className="text-primary text-lg font-mono">{actSubtitle}</p>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-mono">{t('plot_overallProgress')}</span>
            <span className="text-sm font-mono">{project?.progress || 0}%</span>
          </div>
          <div className="relative h-2 bg-secondary rounded-none overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${project?.progress || 0}%` }} />
          </div>
        </div>

        {/* Forge Guide Toggle — hidden for beats with no coaching text */}
        {guideKey !== null && (
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setShowForgeGuide(v => !v)}
              data-testid="forge-guide-toggle"
              className="flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-all"
              style={{
                borderColor: showForgeGuide ? '#E8A020' : undefined,
                color: showForgeGuide ? '#E8A020' : undefined,
                background: showForgeGuide ? 'rgba(232,160,32,0.08)' : undefined,
              }}
            >
              <BookOpen className="w-3.5 h-3.5" />
              {showForgeGuide ? t('hideForgeGuide') : t('showForgeGuide')}
              {showForgeGuide ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        )}

        {/* Enneagram warning — Act II only, feature/novel formats only */}
        {currentLevel === 2 && (formatKey === 'feature' || formatKey === 'novel') && stressWarning?.stress_pattern && (
          <div className="bg-primary/10 border border-primary/30 p-6 mb-8" data-testid="enneagram-warning">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-mono text-sm">!</span>
              </div>
              <div>
                <p className="font-mono text-sm uppercase text-primary mb-2">
                  Enneagram Stress Pattern: {stressWarning.stress_pattern.name}
                </p>
                <p className="text-foreground text-sm leading-relaxed">
                  <strong>Warning:</strong> Under extreme stress, your Lead {stressWarning.stress_pattern.stress}. How does this specific reaction ruin their current strategy in "The Breaking Point" and "The Hollow"?
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Single beat — wizard view */}
        <div className="mb-12">
          <div className="space-y-3">
            <label htmlFor={currentBeat.key} className="block font-mono text-sm uppercase tracking-wider text-foreground">
              {currentBeat.label}
            </label>

            {/* Forge Guide panel */}
            {showForgeGuide && guideText && (
              <div
                className="p-4 text-sm leading-relaxed font-mono border-l-2"
                style={{ borderColor: '#E8A020', background: 'rgba(232,160,32,0.06)', color: '#E8A020' }}
                data-testid={`forge-guide-${currentBeat.key}`}
              >
                <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest opacity-70">
                  <BookOpen className="w-3 h-3" />
                  Forge Guide
                </div>
                {guideText}
              </div>
            )}

            <AutoExpandTextarea
              id={currentBeat.key}
              placeholder={currentBeat.placeholder}
              value={formData[currentBeat.key] || ''}
              onChange={(e) => handleInputChange(currentBeat.key, e.target.value)}
              data-testid={`input-${currentBeat.key}`}
              className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 min-h-[180px]"
            />

            <div className="flex justify-end">
              <button
                onClick={() => navigate(
                  `/scenes?beat_key=${currentBeat.key}&plot_project_id=${projectId}&beat_label=${encodeURIComponent(currentBeat.label)}`
                )}
                data-testid={`build-scenes-${currentBeat.key}`}
                className="flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest border border-border/40 text-muted-foreground hover:text-foreground hover:border-border transition-all"
              >
                <Clapperboard className="w-3.5 h-3.5" />
                {t('plot_buildScenes')}
              </button>
            </div>
          </div>
        </div>

        {/* Beat counter + navigation. Wraps at narrow widths: the counter
            takes its own row and Prev/Next split the second row, so the
            Next Beat button is never clipped at 375px. */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3 sm:gap-4 mb-4">
          <span
            className="w-full text-center order-first sm:order-2 sm:w-auto font-mono text-xs uppercase tracking-widest text-muted-foreground"
            data-testid="beat-counter"
          >
            {t('plot_beatOf', { current: globalBeatIndex + 1, total: totalBeats })}
          </span>

          <Button
            onClick={handlePrev}
            disabled={isFirstBeat || saving}
            data-testid="beat-prev-btn"
            variant="outline"
            className="rounded-none font-mono uppercase tracking-widest text-xs px-4 sm:px-5 py-3 flex-1 sm:flex-none sm:order-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
            {t('previous')}
          </Button>

          <Button
            onClick={handleNext}
            disabled={saving}
            data-testid="beat-next-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs px-4 sm:px-5 py-3 transition-all flex-1 sm:flex-none sm:order-3 min-w-0"
            style={{
              background: '#E8A020',
              color: '#000',
              border: 'none',
            }}
          >
            {isLastBeat ? (
              `${t('plot_complete')} ✓`
            ) : (
              <>
                <span className="truncate">{t('plot_nextBeat')}: {nextBeatLabel}</span>
                <ArrowRight className="w-4 h-4 ml-2 flex-shrink-0" />
              </>
            )}
          </Button>
        </div>

        {/* Save Progress (explicit flush — autosave persists on a debounce) */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-btn"
            variant="outline"
            className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('saving') : t('saveProgress')}
          </Button>
          <SaveStatusLabel status={saveStatus} />
        </div>
      </main>
    </div>
  );
}
